import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import type { CoverImageVariants } from "@/components/blog/ResponsiveCoverImage";
import { HeroBadge } from "@/components/marketing/HeroBadge";
import { HoneycombGrid } from "@/components/brand/HoneycombGrid";
import { ACCENT_COLORS } from "@/components/brand/accentColors";
import { PERSONA_ACCENT_CLASSES } from "@/lib/themeConstants";
import { Link } from "react-router-dom";

interface Props {
  postCount: number;
  avgReadingTime: number;
  featured?: {
    slug: string;
    title: string;
    coverImageUrl: string | null;
    coverImageVariants?: CoverImageVariants | null;
    readingMinutes: number;
  } | null;
}

export function BlogHero({ postCount, avgReadingTime, featured }: Props) {
  const colors = PERSONA_ACCENT_CLASSES.primary;
  void postCount;
  void avgReadingTime;
  return (
    <section
      className={cn(
        "relative overflow-hidden section-py",
        // Compact, breadcrumb-aware. Reuses the marketing `section-py` rhythm
        // so vertical spacing matches /, /temps/, /agencies/, /employers/.
        colors.bgSubtle
      )}
    >
      <HoneycombGrid accentColor={ACCENT_COLORS.default} scale={2.2} opacity={0.18} className="lg:hidden" />
      <HoneycombGrid accentColor={ACCENT_COLORS.default} scale={1.5} opacity={0.18} className="hidden lg:block" />
      <div className="container relative z-10">
        <div className="grid grid-gap-hero lg:grid-cols-[1.4fr_1fr] lg:items-center">
          {/* Left: copy + CTAs */}
          <div className="text-center lg:text-left">
            <HeroBadge icon={Sparkles} text="UK temp staffing insights" color="primary" className="mb-4" />
            <h1 className="text-display mb-5 sm:mb-6">
              Practical UK temp staffing guides
            </h1>
            <p className="text-lead text-muted-foreground mb-4 sm:mb-6 max-w-lg mx-auto lg:mx-0">
              Advice for agencies, employers and temps on shifts, availability,
              timesheets and staffing operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <Button size="lg" asChild className={cn("group", colors.btn)}>
                <a href="#articles">
                  Explore articles
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
          </div>

          {/* Right: text-only featured card. The cover image lives only in
              the grid below to avoid duplicate image fetches on /blog/. */}
          {featured && (
            <div className="hidden lg:block">
              <Link
                to={`/blog/${featured.slug}/`}
                className="group block rounded-2xl border border-border bg-card p-6 shadow-sm hover:border-primary/40 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <p className="text-caption uppercase text-primary font-semibold mb-2">
                  Featured guide · {featured.readingMinutes} min read
                </p>
                <p className="text-h4 text-foreground group-hover:text-primary transition-colors line-clamp-3">
                  {featured.title}
                </p>
                <span className="mt-4 inline-flex items-center text-body-sm text-primary font-medium">
                  Read article
                  <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}